import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Badge, ConfirmModal, Loader, Modal, useToast } from '@lms/ui';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listResource } from '@/lib/api';
import { matchesSearch } from '@/lib/search-normalize';
import { groupDeleteSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface Group {
  id: string;
  name: string;
  description?: string;
  parentId?: string | null;
  parent?: { id: string; name: string } | null;
  membersCount?: number;
  memberCount?: number;
  assignmentsCount?: number;
  childrenCount?: number;
  isActive?: boolean;
}

const FILTER_FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white py-2.5 px-4 text-base outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const PER_PAGE = 10;

function GroupRowActions({ group }: { group: Group }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.GRUPOS_ADMINISTRAR);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const childCount = group.childrenCount ?? 0;

  const deleteMutation = useMutation({
    mutationFn: (children?: 'delete' | 'detach') =>
      api(`/groups/${group.id}${children ? `?children=${children}` : ''}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast(groupDeleteSuccessToast());
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el grupo'));
    },
  });

  return (
    <>
      <div className="flex items-center justify-end gap-1">
        <Link
          to={`/grupos/${group.id}`}
          className="lms-gradebook__action"
          title="Ver"
          aria-label={`Ver ${group.name}`}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            visibility
          </span>
        </Link>
        {canManage && (
          <>
            <Link
              to={`/grupos/${group.id}/edit`}
              className="lms-gradebook__action"
              title="Editar"
              aria-label={`Editar ${group.name}`}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                edit
              </span>
            </Link>
            <button
              type="button"
              className="lms-gradebook__action"
              title="Eliminar"
              aria-label={`Eliminar ${group.name}`}
              onClick={() => setDeleteOpen(true)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                delete
              </span>
            </button>
          </>
        )}
      </div>

      {childCount > 0 ? (
        <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} size="md" footer={null}>
          <h3 className="text-lg font-semibold text-dash-on-surface">Eliminar grupo</h3>
          <p className="mt-2 text-sm leading-relaxed text-dash-on-surface-variant">
            «{group.name}» tiene {childCount} {childCount === 1 ? 'subgrupo' : 'subgrupos'}. Elige si se
            eliminan o si quedan sin padre.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant bg-secondary-200 px-4 py-2.5 text-sm font-medium text-dash-on-surface-variant transition-colors hover:bg-secondary-300 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => deleteMutation.mutate('detach')}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant px-4 py-2.5 text-sm font-medium text-dash-primary transition-colors hover:bg-secondary-100 disabled:opacity-50"
            >
              Dejarlos sin padre
            </button>
            <button
              type="button"
              onClick={() => deleteMutation.mutate('delete')}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center justify-center rounded-lg bg-danger-600 px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:bg-danger-700 disabled:opacity-50"
            >
              Eliminar subgrupos
            </button>
          </div>
        </Modal>
      ) : (
        <ConfirmModal
          open={deleteOpen}
          title="Eliminar grupo"
          description={`¿Está seguro de eliminar el grupo «${group.name}»?`}
          confirmLabel="Eliminar"
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(undefined)}
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </>
  );
}

export function GruposPage() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.GRUPOS_ADMINISTRAR);
  const [search, setSearch] = useState('');
  const [parentId, setParentId] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['groups'],
    queryFn: () => listResource<Group>('/groups'),
  });

  const groups = useMemo(() => {
    let rows = data?.data ?? [];
    if (search.trim()) rows = rows.filter((group) => matchesSearch(group.name, search));
    if (parentId) rows = rows.filter((group) => group.parentId === parentId);
    return rows;
  }, [data?.data, search, parentId]);

  const filteredTotal = groups.length;
  const totalPages = Math.max(1, Math.ceil(filteredTotal / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const firstItem = filteredTotal === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const lastItem = Math.min(currentPage * PER_PAGE, filteredTotal);

  const rows = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return groups.slice(start, start + PER_PAGE);
  }, [groups, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [search, parentId]);

  const parentGroups = useMemo(
    () => (data?.data ?? []).filter((g) => !g.parentId),
    [data?.data],
  );

  function clearFilters() {
    setSearch('');
    setParentId('');
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=users" label="Volver a Administración" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Grupos</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Organización jerárquica para asignaciones masivas
            </p>
          </div>
        </div>
        {canManage && (
          <Link
            to="/grupos/create"
            className="inline-flex items-center gap-2 rounded-lg bg-dash-primary px-6 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 md:ml-14"
          >
            <span className="material-symbols-outlined text-xl" aria-hidden="true">
              group_add
            </span>
            Crear grupo
          </Link>
        )}
      </div>

      <div className="lms-credentials-glass-panel mb-8 rounded-xl border border-dash-outline-variant p-6 shadow-sm">
        <form method="get" action="/grupos" className="grid grid-cols-1 items-end gap-6 md:grid-cols-12" onSubmit={(event) => event.preventDefault()}>
          <div className="md:col-span-5">
            <label htmlFor="group-search" className="mb-2 block text-xs font-semibold text-dash-on-surface-variant">
              Buscar
            </label>
            <SearchInput
              id="group-search"
              name="search"
              value={search}
              onChange={setSearch}
              placeholder="Nombre del grupo..."
              aria-label="Buscar grupo"
            />
          </div>
          <div className="md:col-span-4">
            <label htmlFor="group-parent" className="mb-2 block text-xs font-semibold text-dash-on-surface-variant">
              Grupo padre
            </label>
            <select
              id="group-parent"
              name="parent_id"
              value={parentId}
              onChange={(e) => {
                setParentId(e.target.value);
                setPage(1);
              }}
              className={`${FILTER_FIELD} cursor-pointer`}
            >
              <option value="">Todos</option>
              {parentGroups.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {parent.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 md:col-span-3">
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex flex-1 items-center justify-center rounded-lg bg-secondary-200 px-4 py-2.5 text-sm font-medium text-dash-on-surface-variant transition-all hover:bg-secondary-300"
            >
              Limpiar
            </button>
          </div>
        </form>
      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      <section className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : groups.length === 0 ? (
            <div className="lms-gradebook__table-wrap">
              <div className="lms-gradebook__empty">
                <div className="lms-gradebook__empty-icon" aria-hidden="true">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    group
                  </span>
                </div>
                {search.trim() || parentId ? (
                  <SearchEmptyState
                    term={search.trim()}
                    onClear={() => {
                      setSearch('');
                      setParentId('');
                    }}
                  />
                ) : (
                  <>
                    <h3>Sin grupos</h3>
                    <p>Crea un grupo para organizar usuarios y asignaciones.</p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="lms-gradebook__table-wrap">
                <table className="lms-gradebook__table">
                  <thead>
                    <tr>
                      <th className="lms-gradebook__th" scope="col">
                        Nombre
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Padre
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Miembros
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Asignaciones
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Estado
                      </th>
                      <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((group) => {
                      const members = group.membersCount ?? group.memberCount ?? 0;
                      const initials = group.name
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((part) => part.charAt(0).toUpperCase())
                        .join('');

                      return (
                        <tr key={group.id}>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              <div className="lms-gradebook__avatar" aria-hidden="true">
                                {initials || '?'}
                              </div>
                              <Link to={`/grupos/${group.id}`} className="lms-gradebook__name">
                                {group.name}
                              </Link>
                            </div>
                          </th>
                          <td className="lms-gradebook__td">
                            <span className="lms-gradebook__muted">{group.parent?.name ?? '—'}</span>
                          </td>
                          <td className="lms-gradebook__td">{members}</td>
                          <td className="lms-gradebook__td">{group.assignmentsCount ?? 0}</td>
                          <td className="lms-gradebook__td">
                            <Badge variant={group.isActive === false ? 'default' : 'success'}>
                              {group.isActive === false ? 'Inactivo' : 'Activo'}
                            </Badge>
                          </td>
                          <td className="lms-gradebook__td lms-gradebook__td--actions">
                            <GroupRowActions group={group} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lms-course-forum-card__footer">
                <span className="lms-course-forum-card__meta">
                  Mostrando <strong>{firstItem}</strong> a <strong>{lastItem}</strong> de <strong>{filteredTotal}</strong>{' '}
                  resultados
                </span>
                <div className="lms-course-forum-card__pager">
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página anterior"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página siguiente"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
